// Maps to backend/routers/group_chat.py
import { useState, useEffect, useCallback } from 'react';
import { env } from '../utils/env';

interface ChatMessage {
  id: string;
  user_id: string;
  text: string;
  created_at: string;
  user_name?: string;
  user_avatar?: string;
}

interface Announcement {
  id: string;
  user_id: string;
  text: string;
  pinned: boolean;
  created_at: string;
  updated_at?: string;
  user_name?: string;
  user_avatar?: string;
}

export const useGroupDiscussion = (groupId: string | number, accessToken: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = async (response: Response, defaultMessage: string) => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `${defaultMessage}: ${response.status}`);
  };

  const fetchMessages = useCallback(async () => {
    if (!accessToken || !groupId) return;

    try {
      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/chat`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to fetch chat messages');
      }

      const data = await response.json();

      // Ensure data is an array
      if (Array.isArray(data)) {
        setMessages(data);
      } else {
        console.warn('Chat messages data is not an array:', data);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load chat messages');
    }
  }, [accessToken, groupId]);

  const fetchAnnouncements = useCallback(async () => {
    if (!accessToken || !groupId) return;

    try {
      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/announcements`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to fetch announcements');
      }

      const data = await response.json();

      // Ensure data is an array
      if (Array.isArray(data)) {
        setAnnouncements(data);
      } else {
        console.warn('Announcements data is not an array:', data);
        setAnnouncements([]);
      }
    } catch (err) {
      console.error('Error fetching announcements:', err);
      // Don't set error for announcements as they're optional
    }
  }, [accessToken, groupId]);

  const sendMessage = useCallback(async (text: string) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${env.API_URL}/api/groups/${groupId}/chat`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ text: text.trim() }),
    });

    if (!response.ok) {
      await handleApiError(response, 'Failed to send message');
    }

    const newMessage = await response.json();
    setMessages(prevMessages => [...prevMessages, newMessage]);
    return newMessage;
  }, [accessToken, groupId]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${env.API_URL}/api/groups/${groupId}/chat/${messageId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      await handleApiError(response, 'Failed to delete message');
    }

    setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
    return true;
  }, [accessToken, groupId]);

  const createAnnouncement = useCallback(async (text: string, pinned: boolean = false) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${env.API_URL}/api/groups/${groupId}/announcements`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ text: text.trim(), pinned }),
    });

    if (!response.ok) {
      await handleApiError(response, 'Failed to create announcement');
    }

    const newAnnouncement = await response.json();
    setAnnouncements(prevAnnouncements => [...prevAnnouncements, newAnnouncement]);
    return newAnnouncement;
  }, [accessToken, groupId]);

  const updateAnnouncement = useCallback(async (announcementId: string, text: string, pinned: boolean) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${env.API_URL}/api/groups/${groupId}/announcements/${announcementId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ text: text.trim(), pinned }),
    });

    if (!response.ok) {
      await handleApiError(response, 'Failed to update announcement');
    }

    const updatedAnnouncement = await response.json();
    setAnnouncements(prevAnnouncements =>
      prevAnnouncements.map(ann => ann.id === announcementId ? updatedAnnouncement : ann)
    );
    return updatedAnnouncement;
  }, [accessToken, groupId]);

  const deleteAnnouncement = useCallback(async (announcementId: string) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${env.API_URL}/api/groups/${groupId}/announcements/${announcementId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      await handleApiError(response, 'Failed to delete announcement');
    }

    setAnnouncements(prevAnnouncements =>
      prevAnnouncements.filter(ann => ann.id !== announcementId)
    );
    return true;
  }, [accessToken, groupId]);

  // Initial data load
  useEffect(() => {
    if (accessToken && groupId) {
      setLoading(true);
      setError(null);

      Promise.all([
        fetchMessages(),
        fetchAnnouncements()
      ])
        .catch(err => {
          console.error('Error loading discussion data:', err);
          setError(err instanceof Error ? err.message : 'Failed to load discussion data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [accessToken, groupId, fetchMessages, fetchAnnouncements]);

  // Set up real-time updates
  useEffect(() => {
    if (!accessToken || !groupId) return;

    // Real-time updates with SSE are not currently supported by the backend
    // Implement polling instead for now
    const intervalId = setInterval(() => {
      if (!loading) {
        fetchMessages();
        fetchAnnouncements();
      }
    }, 30000); // Poll every 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [groupId, accessToken, fetchMessages, fetchAnnouncements, loading]);

  return {
    messages,
    announcements,
    loading,
    error,
    sendMessage,
    deleteMessage,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    refresh: useCallback(async () => {
      await Promise.all([fetchMessages(), fetchAnnouncements()]);
    }, [fetchMessages, fetchAnnouncements]),
  };
}; 